import type { SaveStepFields } from './savePayloadFields';

export type ApiLookupRow = {
  id: string;
  name: string;
  code?: string;
  description?: string;
  active?: boolean;
};

export type ApiCategoryRow = ApiLookupRow & {
  parentId?: string;
  parent_id?: string;
  categoryId?: string;
  category_id?: string;
  order_index?: number;
  orderIndex?: number;
  show_in_internal_app?: boolean;
  showInInternalApp?: boolean;
  show_in_external_app?: boolean;
  showInExternalApp?: boolean;
};

export type ApiSubCategoryRow = ApiLookupRow & {
  order_index?: number;
  orderIndex?: number;
  category_id?: string;
  categoryId?: string;
  category_name?: string;
  categoryName?: string;
  category_code?: string;
  categoryCode?: string;
};

export type ApiApplicableAreaRow = ApiLookupRow & {
  order_index?: number;
  orderIndex?: number;
  sub_category_id?: string;
  subCategoryId?: string;
};

export type CourtTypeId = 'MAGISTRATE_COURT' | 'HIGH_COURT';

export const COURT_TYPE_ID = {
  MAGISTRATE_COURT: 'MAGISTRATE_COURT',
  HIGH_COURT: 'HIGH_COURT',
} as const satisfies Record<string, CourtTypeId>;

export type ApiCourtRow = ApiLookupRow & {
  courtType?: string;
  divisionId?: string;
};

export type ApiDistrictRow = ApiLookupRow & {
  provinceId?: string;
};

export type ApiChecklistDocumentRow = {
  id: string;
  active?: boolean;
  optional?: boolean;
  caseCategoryId?: string;
  documentTypeId?: string;
  documentTypeName?: string;
  documentTypeCode?: string;
  documentTypeDescription?: string;
};

/** Applicable area row on criminal steps GET (CMS-CRC-1). */
export type ApplicableAreaRow = {
  applicable_area_id?: string;
  applicable_area_name?: string;
  applicableAreaId?: string;
  applicableAreaName?: string;
};

export type ApplicableAreasField =
  | string
  | string[]
  | ApplicableAreaRow[];

export type CriminalSaveStep1 = {
  subject?: string | null;
  description?: string;
  category_id?: string;
  category_name?: string;
  sub_category_id?: string;
  sub_category_name?: string;
  /** @deprecated API returns at payload root; kept for legacy step GET responses */
  applicable_areas?: ApplicableAreasField;
  applicable_area_names?: string[];
  applicableAreas?: string;
  applicableAreaNames?: string[];
  workflow_instance_id?: string;
  workflow_template_name?: string | null;
  case_source_id?: string;
  case_source_name?: string;
  case_source_description?: string;
  caseSourceDescription?: string;
  province_id?: string;
  province_name?: string;
  district_id?: string;
  district_name?: string;
  high_court_id?: string;
  high_court_name?: string;
  magistrate_court_id?: string;
  magistrate_court_name?: string;
  police_station_id?: string;
  police_station_name?: string;
  external_case_reference_number?: string;
  other_external_case_reference_number?: string;
  case_number?: string;
  has_connected_case?: boolean;
  connected_case_number?: string;
  connected_agd_file_ref_available?: boolean;
  connected_agd_file_ref_number?: string;
};

export type CriminalSaveStep2 = {
  name_of_victim?: string;
  date_of_birth?: string;
  age?: number;
  gender_id?: string;
  gender_name?: string;
};

export type CriminalSaveStep3SuspectName = {
  suspect_name?: string;
  suspect_status_id?: string | null;
};

export type CriminalSaveStep3 = {
  suspects?: Array<{
    id?: string;
    suspect_name?: string;
    name?: string;
    accused_name?: string;
    suspect_status_id?: string;
    suspect_status_name?: string;
    suspect_status_other?: string;
    status_id?: string;
    status_name?: string;
  }> | string[];
  date_of_offence_from?: string;
  date_of_offence_to?: string;
  place_of_offence?: string;
  suspect_status_id?: string;
  suspect_status_name?: string;
  suspect_name?: string;
  accused_details_available?: boolean;
  deadline?: string;
  urgency_level_id?: string;
  urgency_level_name?: string;
  urgency_justification?: string;
  /** Save payload: array of accused rows with name and status per entry. */
  suspect_names?: CriminalSaveStep3SuspectName[] | string[];
};

export type PhysicalFileDeliveryMethodRow = {
  delivery_method_id?: string;
  delivery_method_name?: string;
  other_method?: string | null;
};

export type CriminalSaveStep4 = {
  submitted_by_user_id?: string;
  submitted_by_display_name?: string | null;
  submitted_agency_name?: string;
  submitted_division_name?: string | null;
  designation?: string;
  email?: string;
  contact_no?: string;
  physical_file_delivery_method_ids?: string[];
  physical_file_delivery_methods?: string[] | PhysicalFileDeliveryMethodRow[];
  /** Free-text when only "Other" delivery is selected; null when "Other" is combined with other methods. */
  other_method?: string | null;
  otherMethod?: string | null;
};

export type CriminalSaveStep5File = {
  document_type_id?: string;
  document_type_name?: string;
  file_name?: string;
  original_file_name?: string | null;
  file_size?: number;
  upload_time?: string;
  reason?: string;
};

export type CriminalSaveStep5 = {
  documents?: CriminalSaveStep5File[];
  reason?: string | null;
  uploaded_files?: CriminalSaveStep5File[];
  extra_uploaded_files?: CriminalSaveStep5File[];
};

export type SaveCriminalStepsPayload = {
  case_request_id?: string;
  current_step?: number;
  is_draft_status: boolean;
  confirmed_correct?: boolean;
  is_update_already_submitted?: boolean;
  applicable_areas?: string[] | ApplicableAreaRow[] | null;
  step_1?: SaveStepFields<CriminalSaveStep1>;
  step_2?: SaveStepFields<CriminalSaveStep2>;
  step_3?: SaveStepFields<CriminalSaveStep3>;
  step_4?: SaveStepFields<CriminalSaveStep4>;
  step_5?: SaveStepFields<CriminalSaveStep5>;
};

export type CriminalStepsResponse = {
  case_request_id?: string;
  current_step?: number;
  caseRequestId?: string;
  case_request_criminal_id?: string;
  caseRequestCriminalId?: string;
  reference_no?: string;
  referenceNo?: string;
  fileReferenceNumber?: string;
  status_id?: string;
  status_code?: string;
  status_name?: string;
  completion_remarks?: string;
  applicable_areas?: ApplicableAreasField;
  applicable_area_names?: string[];
  applicableAreas?: string;
  applicableAreaNames?: string[];
  applicableAreaIds?: string[];
  step_1?: CriminalSaveStep1;
  step_2?: CriminalSaveStep2;
  step_3?: CriminalSaveStep3;
  step_4?: CriminalSaveStep4;
  step_5?: CriminalSaveStep5;
  created_at?: string;
  updated_at?: string;
};

export type UploadCaseFileResponse = {
  file_name: string;
  original_file_name?: string;
  upload_time?: string;
  file_size?: number;
  success?: boolean;
};

/** CMS-CRC-6 — DMS file download (base64 in JSON). */
export type CaseFileDownloadResponse = {
  file_name?: string;
  file_size?: number;
  file_content?: string;
  content_type?: string;
  location?: string;
  last_modified?: string;
  success?: boolean;
  message?: string;
};

export type CaseRequestCategoryTree = {
  fileCategoryId: string;
  fileCategoryName: string;
  subCategories: { id: string; name: string; code?: string }[];
};

/** CMS-CRC-7 — submitted checklist row on Documents tab */
export type CaseRequestSubmittedDocument = {
  case_request_document_id?: string;
  document_type_id?: string;
  document_type_name?: string;
  status?: string;
  reason?: string;
  file_name?: string;
  file_size?: number;
  uploaded_at?: string;
};

/** CMS-CRC-7 — additional document row on Documents tab */
export type CaseRequestAdditionalDocument = {
  case_request_document_id?: string;
  document_uploaded_id?: string;
  file_name?: string;
  file_size?: number;
  uploaded_at?: string;
};

/** CMS-CRC-7 — Documents tab payload */
export type CaseRequestDocumentsResponse = {
  case_request_id?: string;
  submitted_documents?: CaseRequestSubmittedDocument[];
  additional_documents?: CaseRequestAdditionalDocument[];
};

/** CMS-CRC-9 — register additional document after temp upload */
export type UploadAdditionalDocumentResponse = {
  case_request_document_id?: string;
  document_uploaded_id?: string;
  file_name?: string;
  file_size?: number;
  uploaded_at?: string;
};

/** CMS-CRC-4 — criminal cover note after submission */
export type CriminalCoverNoteSuspect = {
  suspectName: string;
  suspectStatus: string;
};

export type CriminalCoverNoteResponse = {
  fileReferenceNumber: string;
  requestSubmittedAt: string;
  /** External agency name (extranet submissions). */
  submittedAgency: string;
  /** Intranet submitter division/unit name. */
  submittedDivisionName: string;
  fileCategory: string;
  subFileCategory: string;
  applicableAreas: string;
  description: string;
  urgencyLevel: string;
  deadline: string;
  urgencyJustification: string;
  caseSource: string;
  connectedAgdFileReferenceNumber: string;
  /** Legacy single accused fields when API omits suspects array. */
  accusedOrSuspectName: string;
  accusedOrSuspectStatus: string;
  suspects: CriminalCoverNoteSuspect[];
  documents: string[];
  qr: string;
};
